import { useCallback, useEffect, useRef, useState } from "react";
import { AutoVideo } from "@/components/AutoVideo";

const DURATION_MS = 5000;
const FADE_MS = 600;

/** Ne rejoue pas l'intro si le composant est remonté pendant la même session. */
let introDone = false;

/**
 * Voile d'introduction affiché à l'arrivée sur la page d'accueil.
 * L'animation joue ~5 s pendant que le reste du site se charge en arrière-plan,
 * puis le voile disparaît en fondu. Un minuteur garantit toujours la sortie
 * (autoplay bloqué, vidéo indisponible, connexion lente…).
 */
export function IntroOverlay() {
  const [visible, setVisible] = useState(() => !introDone);
  const [leaving, setLeaving] = useState(false);
  const [videoFailed, setVideoFailed] = useState(false);

  const dismissingRef = useRef(false);

  const dismiss = useCallback(() => {
    if (dismissingRef.current || !visible) return;
    dismissingRef.current = true;
    setLeaving(true);
    window.setTimeout(() => {
      introDone = true;
      setVisible(false);
    }, FADE_MS);
  }, [visible]);

  // Verrouille le scroll uniquement tant que le voile est affiché.
  useEffect(() => {
    const html = document.documentElement;
    if (!visible) {
      html.style.overflow = "";
      return;
    }
    html.style.overflow = "hidden";
    window.scrollTo(0, 0);
    return () => {
      html.style.overflow = "";
    };
  }, [visible]);

  // Minuteur de sortie garanti.
  useEffect(() => {
    if (!visible) return;
    const reduced = window.matchMedia("(prefers-reduced-motion: reduce)").matches;
    // Le compte à rebours part du chargement de la page (la vidéo démarre en
    // autoplay avant l'hydratation), pour une intro d'environ 5 s au total.
    const elapsed = typeof performance !== "undefined" ? performance.now() : 0;
    const delay = reduced ? 300 : Math.max(600, DURATION_MS - elapsed);

    // La lecture auto est fiabilisée par <AutoVideo /> (muted/playsinline posés
    // avant la source, relances sur visibilité et interaction).

    const t1 = window.setTimeout(() => setLeaving(true), delay);
    const t2 = window.setTimeout(() => {
      introDone = true;
      setVisible(false);
    }, delay + FADE_MS);

    return () => {
      window.clearTimeout(t1);
      window.clearTimeout(t2);
    };
  }, [visible]);

  // Fermeture au clavier (Échap) et au clic sur le voile.
  useEffect(() => {
    if (!visible) return;
    const onKeyDown = (e: KeyboardEvent) => {
      if (e.key === "Escape") dismiss();
    };
    window.addEventListener("keydown", onKeyDown);
    return () => window.removeEventListener("keydown", onKeyDown);
  }, [visible, dismiss]);

  if (!visible) return null;

  return (
    <div
      role="dialog"
      aria-modal="true"
      aria-label="Animation d’introduction"
      onClick={dismiss}
      className={`intro-overlay fixed inset-0 z-[9999] flex items-center justify-center transition-opacity ease-out ${
        leaving ? "pointer-events-none opacity-0" : "opacity-100"
      }`}
      style={{ transitionDuration: `${FADE_MS}ms` }}
    >
      <button
        type="button"
        onClick={(e) => {
          e.stopPropagation();
          dismiss();
        }}
        className="absolute top-4 right-4 z-10 rounded-full border border-foreground/10 bg-paper/60 px-3 py-1.5 text-xs uppercase tracking-[0.14em] text-foreground/70 backdrop-blur-sm transition-colors hover:bg-paper hover:text-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ocean"
      >
        Passer l’intro
      </button>

      {videoFailed ? (
        <div className="flex flex-col items-center gap-4" role="status">
          <span
            className="h-12 w-12 animate-spin rounded-full border-2 border-foreground/20 border-t-ocean"
            aria-hidden
          />
          <span className="text-xs uppercase tracking-[0.2em] text-muted-foreground">
            Chargement…
          </span>
        </div>
      ) : (
        <AutoVideo
          src="/intro.mp4"
          loop={false}
          ariaHidden
          onError={() => setVideoFailed(true)}
          onEnded={() => {
            setLeaving(true);
            window.setTimeout(() => {
              introDone = true;
              setVisible(false);
            }, FADE_MS);
          }}
          className="h-[min(70vw,60vh)] w-[min(70vw,60vh)] object-contain"
        />
      )}
    </div>
  );
}
